import React from 'react';
import { useTranslation } from 'react-i18next';

import { BLOG_GEN_ANIM } from '../../../../theme/tokens';
import { GlowAngleProperty, GlowRing, Cell, ThumbFrame, Stack, Caption, Topic } from './GeneratingArticle.styles';

export interface GeneratingArticleProps {
  /** What the owner asked Lumio to write about. */
  topic: string;
  /** The row's position among the generating rows. Staggers the caption and the
   *  border sweep, so two rows never animate in lockstep. */
  index: number;
}

/**
 * The row an article is being written into.
 *
 * Same visual language as the deployment strip's in-progress card: a glow travels
 * around the WHOLE row's edge ({@link GlowRing}) while a status caption types
 * itself where the title will be, over the topic the owner asked for. It is the
 * row's whole "this is happening now" signal, so it lives for exactly as long as
 * the job does and is replaced by the real article the moment the draft exists.
 *
 * This renders into the title cell, but the ring anchors to the `<tr>`: the blog
 * list's `TablePanelBody` makes a row carrying `data-generating` the containing
 * block, which is what lets the border trace every column (see the styles).
 */
export const GeneratingArticle = ({ topic, index }: GeneratingArticleProps): React.ReactElement => {
  const { t } = useTranslation();

  // Literal t() calls so the extractor harvests them and they stay reactive to
  // language; held in a ref so the typewriter never restarts on a re-render.
  const phrases = [
    t('Reading your brief…'),
    t('Outlining the article…'),
    t('Writing the draft…'),
    t('Adding the SEO details…'),
    t('Painting a cover…'),
  ];
  const phrasesRef = React.useRef(phrases);
  phrasesRef.current = phrases;

  const [label, setLabel] = React.useState('');
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduce) {
      const list = phrasesRef.current;
      setLabel(list[index % list.length] ?? '');
      setVisible(true);
      return undefined;
    }

    let phrase = index % phrasesRef.current.length;
    let chars = 0;
    let timer = 0;

    function typeChar(): void {
      const full = phrasesRef.current[phrase] ?? '';
      if (chars < full.length) {
        chars += 1;
        setLabel(full.slice(0, chars));
        timer = window.setTimeout(typeChar, BLOG_GEN_ANIM.typeMs);
      } else {
        timer = window.setTimeout(fadeOut, BLOG_GEN_ANIM.holdMs);
      }
    }
    function fadeOut(): void {
      setVisible(false);
      timer = window.setTimeout(next, BLOG_GEN_ANIM.fadeMs);
    }
    function next(): void {
      phrase = (phrase + 1) % phrasesRef.current.length;
      chars = 0;
      setLabel('');
      setVisible(true);
      timer = window.setTimeout(typeChar, BLOG_GEN_ANIM.gapMs);
    }

    timer = window.setTimeout(typeChar, BLOG_GEN_ANIM.startMs + (index % 4) * 180);
    return () => window.clearTimeout(timer);
  }, [index]);

  return (
    <>
      <GlowAngleProperty />
      {/* `data-generating` is the hook the list's row rule keys off (it makes
          this row the containing block); the ring then traces every column. */}
      <GlowRing data-generating aria-hidden="true" $index={index} />
      <Cell>
        <ThumbFrame aria-hidden="true" />
        <Stack>
          {/* The caption is a PARTIAL phrase while it types, so it renders as its
              own text (i18next falls back to the key); the whole phrases are
              translated at the t() calls above. */}
          <Caption text={label} tag="span" $visible={visible} />
          <Topic text={topic} tag="span" />
        </Stack>
      </Cell>
    </>
  );
};

export default GeneratingArticle;
